import React from "react";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  clear_marketer,
  create_marketer,
  get_marketer,
  update_marketer,
} from "../../../services/apis/marketers";
import { get_companies } from "../../../services/apis/companies";
import { get_countries } from "../../../services/apis/locations";
import useUploadFiles from "../../../hooks/useUploadFiles";
import { IMarketer, IMarketerCreate } from "../../../store/types/marketers";

type MarketerFormValues = Omit<
  IMarketerCreate,
  "company_id" | "experience_years" | "regions" | "commission_rate"
> & {
  company_id: string;
  experience_years: string;
  regions: string;
  commission_rate: string;
};

type MarketerFormErrors = Partial<Record<keyof MarketerFormValues, string>>;
type UploadedAsset = { file_url: string; file_path: string };
type UploadedCv = { id: string; path: string };

const emptyValues: MarketerFormValues = {
  email: "",
  username: "",
  password: "",
  name: "",
  phone_number: "",
  referral_code: "",
  company_id: "",
  company_name: "",
  experience_years: "",
  sales_background: "",
  linkedin_url: "",
  regions: "",
  cv_path: "",
  target_country: "",
  payout_currency: "",
  bvn: "",
  nin: "",
  identity_number: "",
  profile_photo_path: "",
  bank_code: "",
  bank_name: "",
  account_number: "",
  account_name: "",
  bank_verified: "pending",
  status: "pending",
  commission_rate: "0",
};

const isUploadedAssetList = (value: unknown): value is UploadedAsset[] =>
  Array.isArray(value) &&
  value.every(
    (asset) =>
      asset &&
      typeof asset.file_url === "string" &&
      typeof asset.file_path === "string",
  );

const useMarketerForm = (open: boolean, marketer: IMarketer | null) => {
  const dispatch = useAppDispatch();
  const marketerId = marketer?.id;
  const marketerDetails = useAppSelector(
    ({ marketer: state }) => state.marketer,
  );
  const loading = useAppSelector(({ marketer: state }) => state.loading);
  const { companies, companiesLoading } = useAppSelector(
    ({ companies: state }) => ({
      companies: state.companies,
      companiesLoading: state.loading,
    }),
  );
  const countries = useAppSelector(({ locations }) => locations.countries);
  const locationsLoading = useAppSelector(({ locations }) => locations.loading);
  const [values, setValues] = React.useState<MarketerFormValues>(emptyValues);
  const [errors, setErrors] = React.useState<MarketerFormErrors>({});
  const countriesRequested = React.useRef(false);
  const companiesRequested = React.useRef(false);
  const [uploadedPhotoPath, setUploadedPhotoPath] = React.useState("");
  const [photoPreview, setPhotoPreview] = React.useState("");
  const [uploadedCv, setUploadedCv] = React.useState<UploadedCv | null>(null);
  const [cvPreview, setCvPreview] = React.useState("");
  const {
    loader: photoLoading,
    uploadFile: uploadPhoto,
    deleteFiles: deletePhoto,
  } = useUploadFiles();
  const {
    loader: cvLoading,
    uploadFile: uploadCvFiles,
    deleteFiles: deleteCvFile,
  } = useUploadFiles();

  React.useEffect(() => {
    if (!open) return;

    setErrors({});
    setUploadedPhotoPath("");
    setPhotoPreview("");
    setUploadedCv(null);
    setCvPreview("");
    if (marketerId == null) {
      clear_marketer(dispatch);
      setValues(emptyValues);
      return;
    }

    clear_marketer(dispatch);
    void get_marketer(dispatch, String(marketerId));
  }, [dispatch, marketerId, open]);

  React.useEffect(() => {
    if (!open) {
      countriesRequested.current = false;
      return;
    }
    if (countries.length || locationsLoading || countriesRequested.current)
      return;

    countriesRequested.current = true;
    void get_countries(dispatch);
  }, [countries.length, dispatch, locationsLoading, open]);

  React.useEffect(() => {
    if (!open) {
      companiesRequested.current = false;
      return;
    }
    if (companies.length || companiesLoading || companiesRequested.current)
      return;

    companiesRequested.current = true;
    void get_companies(dispatch);
  }, [companies.length, companiesLoading, dispatch, open]);

  React.useEffect(() => {
    if (!open || !marketer || marketerDetails?.id !== marketer.id) return;

    const application = marketerDetails.application;
    setPhotoPreview(application.profile_photo_path ?? "");
    setCvPreview(application.cv_path ?? "");
    setValues({
      email: marketerDetails.email ?? "",
      username: marketerDetails.username ?? "",
      password: "",
      name: marketerDetails.name ?? "",
      phone_number: marketerDetails.phone_number ?? "",
      referral_code: marketerDetails.referral_code ?? "",
      company_id:
        application.company_id == null ? "" : String(application.company_id),
      company_name: application.company_name ?? "",
      experience_years:
        application.experience_years == null
          ? ""
          : String(application.experience_years),
      sales_background: application.sales_background ?? "",
      linkedin_url: application.linkedin_url ?? "",
      regions: application.regions?.join(", ") ?? "",
      cv_path: application.cv_path ?? "",
      target_country: application.target_country ?? "",
      payout_currency: application.payout_currency ?? "",
      bvn: application.bvn ?? "",
      nin: application.nin ?? "",
      identity_number: application.identity_number ?? "",
      profile_photo_path: application.profile_photo_path ?? "",
      bank_code: application.bank_code ?? "",
      bank_name: application.bank_name ?? "",
      account_number: application.account_number ?? "",
      account_name: application.account_name ?? "",
      bank_verified: application.bank_verified ?? "pending",
      status: application.status ?? "pending",
      commission_rate:
        application.commission_rate == null
          ? "0"
          : String(application.commission_rate),
    });
    setErrors({});
  }, [marketer, marketerDetails, open]);

  const setFieldValue = (field: keyof MarketerFormValues, value: string) => {
    setValues((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => ({ ...previous, [field]: "" }));
  };

  const onChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target;
    if (Object.prototype.hasOwnProperty.call(emptyValues, name)) {
      setFieldValue(name as keyof MarketerFormValues, value);
    }
  };

  const onPhotoChange = async (file: File | File[]) => {
    if (Array.isArray(file)) return;
    if (
      !["image/jpeg", "image/png", "image/webp"].includes(file.type) ||
      file.size > 10 * 1024 * 1024
    ) {
      setErrors((previous) => ({
        ...previous,
        profile_photo_path: "Choose a JPG, PNG, or WebP image under 10MB.",
      }));
      return;
    }
    const result: unknown = await uploadPhoto(file, "onboarding", "image");
    if (!isUploadedAssetList(result) || !result.length) {
      setErrors((previous) => ({
        ...previous,
        profile_photo_path: "Profile photo upload failed. Please try again.",
      }));
      return;
    }

    const asset = result[0];
    setUploadedPhotoPath(asset.file_path);
    setPhotoPreview(asset.file_url);
    setFieldValue("profile_photo_path", asset.file_path);
  };

  const onCvChange = async (files: { id: string; file: File }[]) => {
    const selected = files[files.length - 1];
    if (!selected) return;

    const result: unknown = await uploadCvFiles(
      selected.file,
      "onboarding",
      "document",
    );
    if (!isUploadedAssetList(result) || !result.length) {
      setErrors((previous) => ({
        ...previous,
        cv_path: "CV upload failed. Please try again.",
      }));
      return;
    }

    const asset = result[0];
    setUploadedCv({ id: selected.id, path: asset.file_path });
    setCvPreview(asset.file_url);
    setFieldValue("cv_path", asset.file_path);
  };

  const onPhotoDelete = async () => {
    if (uploadedPhotoPath) {
      const result = await deletePhoto(uploadedPhotoPath);
      if (!result) return;
    }
    setUploadedPhotoPath("");
    setPhotoPreview("");
    setFieldValue("profile_photo_path", "");
  };

  const onCvDelete = async (fileId: string) => {
    if (!uploadedCv || uploadedCv.id !== fileId) return;
    const result = await deleteCvFile(uploadedCv.path);
    if (!result) return;
    setUploadedCv(null);
    setCvPreview("");
    setFieldValue("cv_path", "");
  };

  const validate = () => {
    const nextErrors: MarketerFormErrors = {};
    const experience = Number(values.experience_years);
    const commission = Number(values.commission_rate);
    const phoneDigits = values.phone_number.replace(/\D/g, "");

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (!/^[a-zA-Z0-9_.-]{3,30}$/.test(values.username.trim())) {
      nextErrors.username = "Enter a valid username (3-30 characters).";
    }
    if (!marketer && values.password.length < 8) {
      nextErrors.password = "Password must contain at least 8 characters.";
    }
    if (values.name.trim().length < 2) {
      nextErrors.name = "Enter a name with at least 2 characters.";
    }
    if (
      !/^\+?[0-9\s().-]+$/.test(values.phone_number.trim()) ||
      phoneDigits.length < 7 ||
      phoneDigits.length > 15
    ) {
      nextErrors.phone_number = "Enter a valid phone number.";
    }
    if (!values.company_name.trim()) {
      nextErrors.company_name = "Enter a company name or choose a company.";
    }
    if (
      values.company_id &&
      (!Number.isInteger(Number(values.company_id)) ||
        Number(values.company_id) < 1)
    ) {
      nextErrors.company_id = "Select a valid company.";
    }
    if (!Number.isInteger(experience) || experience < 0 || experience > 60) {
      nextErrors.experience_years =
        "Enter a whole number of years between 0 and 60.";
    }
    if (!values.sales_background.trim()) {
      nextErrors.sales_background = "Enter the sales background.";
    }
    if (!values.linkedin_url.trim()) {
      nextErrors.linkedin_url = "Enter a LinkedIn URL.";
    } else {
      try {
        const url = new URL(values.linkedin_url.trim());
        if (url.protocol !== "https:" && url.protocol !== "http:") {
          nextErrors.linkedin_url = "Enter a valid LinkedIn URL.";
        }
      } catch {
        nextErrors.linkedin_url = "Enter a valid LinkedIn URL.";
      }
    }
    if (!values.regions.split(",").some((region) => region.trim())) {
      nextErrors.regions = "Enter at least one region.";
    }
    if (!values.cv_path) nextErrors.cv_path = "Upload a CV.";
    if (!values.target_country.trim()) {
      nextErrors.target_country = "Enter a target country.";
    }
    if (!/^[A-Za-z]{3}$/.test(values.payout_currency.trim())) {
      nextErrors.payout_currency = "Enter a 3-letter currency code.";
    }
    if (
      !values.bvn.trim() &&
      !values.nin.trim() &&
      !values.identity_number.trim()
    ) {
      nextErrors.identity_number =
        "Enter at least one of BVN, NIN, or identity number.";
    }
    if (!values.profile_photo_path) {
      nextErrors.profile_photo_path = "Upload a profile photo.";
    }
    if (!values.bank_code.trim()) nextErrors.bank_code = "Enter a bank code.";
    if (!values.bank_name.trim()) nextErrors.bank_name = "Enter a bank name.";
    if (!/^\d{6,20}$/.test(values.account_number.trim())) {
      nextErrors.account_number =
        "Enter an account number containing 6 to 20 digits.";
    }
    if (!values.account_name.trim()) {
      nextErrors.account_name = "Enter the account name.";
    }
    if (
      values.commission_rate.trim() === "" ||
      !Number.isFinite(commission) ||
      commission < 0 ||
      commission > 100
    ) {
      nextErrors.commission_rate = "Enter a commission rate from 0 to 100.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const submit = async () => {
    if (!validate()) return false;

    const payload: IMarketerCreate = {
      email: values.email.trim(),
      username: values.username.trim(),
      password: values.password,
      name: values.name.trim(),
      phone_number: values.phone_number.trim(),
      referral_code: values.referral_code.trim(),
      company_id: values.company_id ? Number(values.company_id) : null,
      company_name: values.company_name.trim(),
      experience_years: Number(values.experience_years),
      sales_background: values.sales_background.trim(),
      linkedin_url: values.linkedin_url.trim(),
      regions: values.regions
        .split(",")
        .map((region) => region.trim())
        .filter(Boolean),
      cv_path: values.cv_path,
      target_country: values.target_country.trim(),
      payout_currency: values.payout_currency.trim().toUpperCase(),
      bvn: values.bvn.trim(),
      nin: values.nin.trim(),
      identity_number: values.identity_number.trim(),
      profile_photo_path: values.profile_photo_path,
      bank_code: values.bank_code.trim(),
      bank_name: values.bank_name.trim(),
      account_number: values.account_number.trim(),
      account_name: values.account_name.trim(),
      bank_verified: values.bank_verified,
      status: values.status,
      commission_rate: Number(values.commission_rate),
    };

    if (marketer) {
      const { password, ...updatePayload } = payload;
      return update_marketer(
        dispatch,
        marketer.id,
        password ? payload : updatePayload,
      );
    }
    return create_marketer(dispatch, payload);
  };

  return {
    values,
    errors,
    companies,
    countries,
    photoPreview,
    cvPreview,
    loading: loading || photoLoading || cvLoading || locationsLoading,
    onChange,
    setFieldValue,
    onPhotoChange,
    onPhotoDelete,
    onCvChange,
    onCvDelete,
    submit,
  };
};

export default useMarketerForm;
